import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useRef,
  useState,
} from 'react';

import useBreakpoints from '@/common/hooks/useBreakpoints';
import {
  HeaderMenuContextDefaultProps,
  HeaderMenuContextPropTypes,
} from '@/ebat/contexts/Header/HeaderMenuContext/HeaderMenuContext.proptypes';

export const MENU_NAME_PRODUCTS = 'products';
export const MENU_NAME_NOTIFICATIONS = 'notifications';
export const MENU_NAME_MOBILE = 'mobile';
export const MENU_NAME_USER = 'user';
export const MENU_NAME_SEARCH = 'search';

export const MENU_TYPE_DRAWER = 'drawer';
export const MENU_TYPE_FIXED = 'fixed-panel';
export const MENU_TYPE_EXTERNAL = 'external';

const HeaderMenuContext = createContext(null);

export const HeaderMenuContextProvider = ({ children }) => {
  const { isMaxDesktop } = useBreakpoints();
  const prevIsMaxDesktopRef = useRef(isMaxDesktop);
  const [menuName, setMenuName] = useState(null);
  const [menuType, setMenuType] = useState(null);
  const [menuPlacement, setMenuPlacement] = useState({});
  const [menuWidth, setMenuWidth] = useState(null);
  const [menuContent, setMenuContent] = useState(null);

  const handleClose = () => {
    setMenuName(null);
    setMenuType(null);
    setMenuContent(null);
    setMenuWidth(null);
  };

  const handleOpen = (
    name,
    type,
    placement = null,
    content = null,
    width = null,
  ) => {
    setMenuName(name);
    setMenuType(type);
    setMenuPlacement((prev) => ({
      ...prev,
      [type]: placement,
    }));
    setMenuContent(content);
    setMenuWidth(width);
  };

  const isOpen = useCallback(
    (arg) => {
      if (arg?.name === undefined && arg?.type === undefined) {
        return menuName !== null;
      }
      if (arg?.name !== undefined && arg?.type === undefined) {
        return menuName === arg.name;
      }
      if (arg?.name === undefined && arg?.type !== undefined) {
        return menuType === arg.type;
      }

      return menuName === arg.name && menuType === arg.type;
    },
    [menuName, menuType],
  );

  const getPlacement = (type) => menuPlacement?.[type] ?? 'left';

  useEffect(() => {
    if (prevIsMaxDesktopRef.current !== isMaxDesktop) {
      if (
        isOpen({ name: MENU_NAME_NOTIFICATIONS }) ||
        isOpen({ name: MENU_NAME_MOBILE }) ||
        isOpen({ name: MENU_NAME_USER })
      ) {
        handleClose();
      }
      prevIsMaxDesktopRef.current = isMaxDesktop;
    }
  }, [isMaxDesktop, isOpen]);

  return (
    <HeaderMenuContext.Provider
      value={{
        isOpen,
        handleOpen,
        handleClose,
        getPlacement,
        setWidth: setMenuWidth,
        width: menuWidth,
        name: menuName,
        content: menuContent,
      }}
    >
      {children}
    </HeaderMenuContext.Provider>
  );
};

export const useHeaderMenuContext = () => useContext(HeaderMenuContext);

HeaderMenuContextProvider.defaultProps = {
  ...HeaderMenuContextDefaultProps,
};

HeaderMenuContextProvider.propTypes = {
  ...HeaderMenuContextPropTypes,
};
